import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { Messaging, getToken, onMessage, deleteToken } from "@angular/fire/messaging";
import { Observable, tap } from "rxjs";
import { ToastService } from "./toast.service";
import { environment } from "../../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class FcmService {
  private readonly toastSrv = inject(ToastService);
  private readonly http = inject(HttpClient);
  private readonly msg = inject(Messaging);
  private readonly injector = inject(EnvironmentInjector);

  message$: Observable<any>;

  constructor() {
    this.requestNotificationPermission();

    // Ejecutar en un contexto de inyección
    this.message$ = new Observable((sub) => {
      runInInjectionContext(this.injector, () => {
        onMessage(this.msg, (msg) => sub.next(msg));
      });
    }).pipe(
      tap((msg: any) => {
        console.log("Received Firebase Cloud Message:", msg);
        this.toastSrv.show({
          type: 'success',
          message: msg?.notification?.title || 'New FCM Message!',
        });
      })
    );

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.action === 'reload') {
          window.location.reload(); // Recargar la página
        }
      });
    }
  }

  private requestNotificationPermission() {
    Notification.requestPermission()
      .then((permission: NotificationPermission) => {
        if (permission === "granted") {
          console.log("Notification permission granted.");
        } else if (permission === "denied") {
          console.log("Notification permission denied.");
        }
      })
      .catch((err) => {
        console.error("Error requesting notification permission:", err);
      });
  }

  registerServiceWorker(contextId: string) {
    navigator.serviceWorker
      .register("scripts/firebase-messaging-sw.js", {
        type: "module",
      })
      .then((serviceWorkerRegistration) => {
        this.getToken(this.injector, serviceWorkerRegistration, contextId);
      })
      .catch((err) => {
        console.error("Error registering service worker:", err);
      });

    navigator.serviceWorker.ready.then((registration) => {
      console.log('Service Worker is active and controlling clients.');
    });
  }

  private async getToken(injector: EnvironmentInjector, serviceWorkerRegistration: ServiceWorkerRegistration, contextId: string) {
    runInInjectionContext(injector, async () => {
      const token = await getToken(this.msg, {
        vapidKey: environment.fcmVapid,
        serviceWorkerRegistration,
      });

      if (token) {
        console.debug("FCM Token:", token);
        this.subscribeToTopic(token, contextId);
      } else {
        console.warn("No FCM token received. Ensure Firebase setup is correct.");
      }
    }).catch((err) => {
      console.error("Error getting FCM token:", err);
    });
  }

  async deleteToken() {
    try {
      await deleteToken(this.msg);
      console.log("FCM token deleted successfully.");
      // Ensure you also remove the token from your backend if stored
    } catch (err) {
      console.error("Error deleting FCM token:", err);
    }
  }

  private subscribeToTopic(token: string, topic: string) {
    if (this.isSubscribedToTopic(topic)) {
      console.log(`Already subscribed to ${topic}`);
      return;
    }

    const url = `${environment.apiGatewayUrl}/docmanager/api/v1/documents/subscribe`;
    const body = { token, topic };

    this.http.post(url, body).subscribe({
      next: () => {
        console.log(`Successfully subscribed to topic: ${topic}`);
        this.saveSubscriptionState(topic, true); // Aggiorna lo stato
      },
      error: (err) => {
        console.error('Error subscribing to topic:', err);
      }
    });
  }

  // Funzione per disiscriversi da un topic
  private unsubscribeFromTopic(topic: string) {
    if (!this.isSubscribedToTopic(topic)) {
      console.log(`Not subscribed to ${topic}`);
      return;
    }

    // Chiama api per disinscriversi dal topic
  }

  // Funzione per verificare lo stato di iscrizione
  private isSubscribedToTopic(topic: string) {
    const subscriptions = JSON.parse(localStorage.getItem("fcmSubscriptions") || "{}");
    return subscriptions[topic] === true;
  }

  // Funzione per salvare lo stato di iscrizione
  private saveSubscriptionState(topic: string, isSubscribed: boolean) {
    const subscriptions = JSON.parse(localStorage.getItem("fcmSubscriptions") || "{}");
    subscriptions[topic] = isSubscribed;
    localStorage.setItem("fcmSubscriptions", JSON.stringify(subscriptions));
  }

}
